import { useTranslations } from '@vocab/react';
import React from 'react';

import { useAppContext } from '../../AppContext';
import { useLoginPageContext } from '../../pages/LoginPage/LoginPageContext';

import translations from './.vocab';
import CareerGuideNudge from './nudges/CareerGuideNudge/CareerGuideNudge';
import CommonNudge from './nudges/CommonNudge';
import ExperienceNudge from './nudges/ExperienceNudge/ExperienceNudge';
import JobApplyNudge from './nudges/JobApplyNudge/JobApplyNudge';
import SeekPassNudge from './nudges/SeekPassNudge/SeekPassNudge.tsx';
import isApplyNudge from './utils/isApplyNudge';

interface Props {
  experience?: boolean;
  apply?: boolean;
}

const Nudge = React.memo(
  (
    { experience = false }: Props = {
      experience: false,
    },
  ) => {
    const { t } = useTranslations(translations);
    const { nudge, applyContext } = useLoginPageContext();
    const { enableNudge, locale, isMobileClient } = useAppContext();

    if (!enableNudge || isMobileClient) {
      return null;
    }

    if (experience) {
      return <ExperienceNudge />;
    }

    if (isApplyNudge(nudge)) {
      return <JobApplyNudge applyContext={applyContext} />;
    }

    if (nudge === 'careerGuide') {
      return <CareerGuideNudge />;
    }

    if (nudge === 'certsy' || nudge === 'seekpass') {
      return <SeekPassNudge />;
    }

    const getNudgeText = () => {
      switch (nudge) {
        case 'application':
          return t('Sign in to continue your application');
        case 'appliedJobs':
          return t('Sign in to see your applied jobs');
        case 'profile':
          return locale === 'nz'
            ? t('Please sign in to view your profile')
            : t('Be seen with a SEEK Profile. Sign in to view your profile');
        case 'recommended':
          return t('Sign in to see your recommended jobs');
        case 'savedJobs':
          return t('Sign in to see your saved jobs');
        case 'savedSearch':
          return t('Sign in to see your saved searches');
        case 'saveJob':
          return t('Sign in to save jobs');
        default:
          return null;
      }
    };
    const nudgeText = getNudgeText();

    if (nudgeText) {
      return <CommonNudge text={nudgeText} />;
    }

    return <ExperienceNudge />;
  },
);

export default Nudge;
